import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'
import type { SortingRule } from 'react-table'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { fetchProfilesListThunk, profilesListActions } from './slice'

import { CommonSearch, MobileSortBy } from 'components/Table'
import SpinnerCentered from 'components/Loader/SpinnerCentered'
import ProfilesTable from './components/ProfilesTable'
import { ROOT_USER } from 'features/auth/permissions/consts'
import { userAnyTopDomainIdSelector } from 'features/auth/permissions/selectors'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import ProfileUploadButton from './components/ProfileUploadButton'
import type { Profile } from './types'
import { H4 } from 'components/Typography'
import { profilesPageMessages } from 'features/profiles/messages'
import { commonMessages } from 'app/i18n/messages'
import { PROFILES_HEADERS } from './consts'
import { getSortOptions } from 'components/Table/utils'
import usePermissions from 'features/auth/permissions/usePermissions'
import {
  profilesListErrorSelector,
  profilesListIsFetchingSelector,
  profilesListIsFinishedSelector,
  profilesListSelector,
} from './selectors'
import {
  ProfilesSearchWrapper,
  StyledNoProfiles,
  StyledProfilesContainer,
  StyledProfilesHeader,
} from './styles'

function Profiles() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [hasRootUserPermission] = usePermissions({ allow: [ROOT_USER] })

  const [searchValue, setSearchValue] = useState<string>()

  const userAnyTopDomainId = useAppSelector(userAnyTopDomainIdSelector)
  const profilesList = useAppSelector<Profile[]>(profilesListSelector)
  const isFetching = useAppSelector(profilesListIsFetchingSelector)
  const isFinished = useAppSelector(profilesListIsFinishedSelector)
  const error = useAppSelector(profilesListErrorSelector)

  useEffect(() => {
    if (userAnyTopDomainId) {
      dispatch(fetchProfilesListThunk(userAnyTopDomainId))
    }

    return () => {
      dispatch(profilesListActions.clear())
    }
  }, [dispatch, userAnyTopDomainId])

  const headers = useMemo(
    () => ({
      thumbnail: {
        label: '',
        id: PROFILES_HEADERS.THUMBNAIL,
      },
      manufacturer: {
        label: formatMessage(profilesPageMessages.manufacturerColumn),
        id: PROFILES_HEADERS.MANUFACTURER,
      },
      model: {
        label: formatMessage(profilesPageMessages.modelColumn),
        id: PROFILES_HEADERS.MODEL,
      },
      deviceClass: {
        label: formatMessage(profilesPageMessages.deviceClassColumn),
        id: PROFILES_HEADERS.DEVICE_CLASS,
      },
      uploaded: {
        label: formatMessage(profilesPageMessages.uploadedColumn),
        id: PROFILES_HEADERS.UPLOADED,
      },
      version: {
        label: formatMessage(profilesPageMessages.versionColumn),
        id: PROFILES_HEADERS.VERSION,
      },
      downloadLink: {
        label: '',
        id: PROFILES_HEADERS.DOWNLOAD_LINK,
      },
      DeleteButton: {
        label: '',
        id: PROFILES_HEADERS.ID,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(
    () =>
      getSortOptions(headers).filter(
        (option) =>
          ![
            PROFILES_HEADERS.THUMBNAIL,
            PROFILES_HEADERS.ID,
            PROFILES_HEADERS.DOWNLOAD_LINK,
          ].includes(option.id as PROFILES_HEADERS)
      ),
    [headers]
  )
  const [sortBy, setSortBy] = useState<SortingRule<Profile>>(sortOptions[0])

  return (
    <StyledProfilesContainer>
      <StyledProfilesHeader>
        <H4>{formatMessage(profilesPageMessages.title)}</H4>
        {hasRootUserPermission && <ProfileUploadButton />}
      </StyledProfilesHeader>

      <ProfilesSearchWrapper>
        <CommonSearch
          setSearchValue={setSearchValue}
          placeholder={formatMessage(profilesPageMessages.searchPlaceholder)}
        />
      </ProfilesSearchWrapper>

      <MobileSortBy<Profile>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {isFetching && <SpinnerCentered />}
      {error && (
        <StyledNoProfiles>
          {formatMessage(commonMessages.commonError)}
        </StyledNoProfiles>
      )}
      {isFinished && isEmpty(profilesList) && (
        <StyledNoProfiles>
          {formatMessage(profilesPageMessages.noProfilesFoundMessage)}
        </StyledNoProfiles>
      )}
      {isFinished && !isEmpty(profilesList) && (
        <CurrentUserPermissionsContext.Provider
          value={{ hasRootUserPermission }}
        >
          <ProfilesTable
            data={profilesList}
            searchValue={searchValue}
            headers={headers}
            defaultSortBy={sortOptions[0]}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </CurrentUserPermissionsContext.Provider>
      )}
    </StyledProfilesContainer>
  )
}

export default Profiles
